import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import produce from 'immer'
import { toast } from 'react-toastify'
import Button from 'src/components/base/Button'
import Input from 'src/components/base/Input'
import Modal from 'src/components/app/customModal/Modal'
import BerthPicker from 'src/components/configuration/editor/berthEditor/berthSelector/berthPicker/BerthPicker'
import { Berth, LatLon, Quay, RootState, TerminalPlannerBerth } from 'src/types'

import { getBerthErrors } from '../utils'

import './BerthModal.scss'

export type TPropsBerthModal = {
  selectedBerth: TerminalPlannerBerth | null
  selectedQuay: Quay
  isOpen: boolean
  onBerthConfirm: (berth: TerminalPlannerBerth | undefined) => void
  onCancel?: () => void
  customFetch: (url: string, options: RequestInit, customResolver?: (r: Response) => any) => Promise<any>
}

function BerthModal({ selectedBerth, selectedQuay, onBerthConfirm, isOpen, onCancel, customFetch }: TPropsBerthModal) {
  const appConfig = useSelector((state: RootState) => state.app.present.config)
  const auth = useSelector((state: RootState) => state.login.auth)
  const [berth, setBerth] = useState<TerminalPlannerBerth | null>(selectedBerth)
  const isEditBerth = !!selectedBerth?.berthId
  const title = isEditBerth ? 'Edit berth' : 'Add berth'

  const errorCheckingHasError = useCallback(() => {
    const errors = getBerthErrors(berth, selectedQuay)
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error))
      return true
    }
    return false
  }, [berth, selectedQuay])

  const onConfirm = useCallback(() => {
    if (errorCheckingHasError()) {
      return
    }
    onBerthConfirm(berth)
  }, [berth, errorCheckingHasError, onBerthConfirm])

  return (
    <Modal.Wrapper isOpen={isOpen} title={title} onCloseModal={onCancel}>
      <Modal.Content>
        <div className="tq-modal-content w-screen-lg">
          <form className="tq-form">
            <div className="tq-form-group">
              <Input
                label="Name"
                id="berth-name"
                value={berth?.terminalName ?? ''}
                onChange={e => {
                  const newTerminalName = e.target.value
                  setBerth(
                    produce((draft: TerminalPlannerBerth) => {
                      draft.terminalName = newTerminalName
                    })
                  )
                }}
                required
                flexibleWidth
                parentStyle={{ flex: 1 }}
              />
              <Input
                label="Bollard start"
                id="bollard-start"
                type="number"
                value={berth?.startBollard ?? ''}
                onChange={e => {
                  const newStartBollard = Number(e.target.value)
                  setBerth(
                    produce((draft: TerminalPlannerBerth) => {
                      draft.startBollard = newStartBollard
                    })
                  )
                }}
                required
                flexibleWidth
                parentStyle={{ flex: 1 }}
              />
              <Input
                label="Bollard end"
                id="bollard-end"
                type="number"
                value={berth?.endBollard ?? ''}
                onChange={e => {
                  const newEndBollard = Number(e.target.value)
                  setBerth(
                    produce((draft: TerminalPlannerBerth) => {
                      draft.endBollard = newEndBollard
                    })
                  )
                }}
                required
                flexibleWidth
                parentStyle={{ flex: 1 }}
              />
            </div>
            <div className="tq-form-group">
              <BerthPicker
                terminalCenter={appConfig.center}
                selectedBerth={berth?.berthId}
                selectedBerthLatLngs={berth?.berth?.area?.coordinates?.[0]?.map(coord => [coord[1], coord[0]])}
                onSelectBerth={(selectedBerth: string, berth: Berth) => {
                  if (!selectedBerth) {
                    toast.error(
                      `Berth ${berth.berthName} is currently unavailable. Please contact support@teqplay.com for assistance.`
                    )
                    return
                  }
                  setBerth(
                    produce((draft: TerminalPlannerBerth) => {
                      draft.berthId = selectedBerth
                      draft.berth = berth
                      draft.terminalName = berth.berthNameLong
                    })
                  )
                }}
                token={auth.token}
                customFetch={customFetch}
              />
            </div>
            <div className="tq-form-group align-right">
              <Button variant="neutral-tertiary" type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={onConfirm}>
                Confirm
              </Button>
            </div>
          </form>
        </div>
      </Modal.Content>
    </Modal.Wrapper>
  )
}

export default BerthModal
